import { isProductSupplierProfessional } from '~/modules/professional/util/is-product-supplier-professional';

interface ProfessionalEnquiryContext {
    IsManufacturer?: boolean;
    HasProducts?: boolean;
    ProductsAllowed?: boolean;
}

/**
 * Generates the default enquiry message based on the item and CTA context.
 */
export function getDefaultEnquiryMessage({
    itemType,
    itemTitle,
    ctaLabel,
    professional,
}: {
    itemType: 'professional' | 'product' | 'project' | 'article';
    itemTitle?: string;
    ctaLabel?: string;
    professional?: ProfessionalEnquiryContext;
}) {
    if (!itemTitle) {
        return '';
    }

    const normalisedCta = ctaLabel?.toLowerCase() ?? '';

    if (normalisedCta.includes('sample')) {
        return (
            `Hi, I'm interested in requesting a sample of ${itemTitle}.` +
            `\n\nCould you please let me know about sample availability` +
            ` and any associated costs?`
        );
    }

    if (normalisedCta.includes('enquire')) {
        return (
            `Hi, I'm interested in ${itemTitle} and would like` +
            ` to discuss this further.` +
            `\n\nCould you please get in touch?`
        );
    }

    if (normalisedCta.includes('question')) {
        return (
            `Hi, I have a question about ${itemTitle}.` +
            `\n\nCould you please get in touch?`
        );
    }

    if (normalisedCta.includes('meeting')) {
        return (
            `Hi, I'd like to request a meeting with ${itemTitle}` +
            ` to discuss a potential project.` +
            `\n\nCould you please let me know your availability?`
        );
    }

    if (normalisedCta.includes('consultation')) {
        return (
            `Hi, I'd like to book a consultation with ${itemTitle}` +
            ` to discuss my project requirements.` +
            `\n\nCould you please let me know your availability?`
        );
    }

    if (normalisedCta.includes('quote')) {
        return (
            `Hi, I came across your project "${itemTitle}"` +
            ` and I'm interested in something similar.` +
            `\n\nCould you please provide a rough estimate` +
            ` and let me know the best way to discuss this further?`
        );
    }

    if (normalisedCta.includes('price') || normalisedCta.includes('brochure')) {
        return (
            `Hi, I'm interested in ${itemTitle} and would like` +
            ` to request pricing information and a brochure.` +
            `\n\nCould you please send these through?`
        );
    }

    switch (itemType) {
        case 'product':
            return (
                `Hi, I'm interested in ${itemTitle} and would like` +
                ` to learn more.` +
                `\n\nCould you please send me pricing` +
                ` and availability information?`
            );
        case 'project':
            return (
                `Hi, I came across your project "${itemTitle}"` +
                ` and would love to learn more.` +
                `\n\nCould you please share further details` +
                ` and indicative pricing?`
            );
        case 'professional':
            if (
                isProductSupplierProfessional({
                    isManufacturer: professional?.IsManufacturer,
                    hasProducts: professional?.HasProducts,
                    productsAllowed: professional?.ProductsAllowed,
                })
            ) {
                return (
                    `Hi, I'm interested in learning more about` +
                    ` ${itemTitle}'s products and would like to discuss` +
                    ` the best options for my project.` +
                    `\n\nCould you please get in touch?`
                );
            }
            return (
                `Hi, I'm interested in learning more about` +
                ` ${itemTitle}'s services and would like to discuss` +
                ` a potential project.` +
                `\n\nCould you please get in touch?`
            );
        default:
            return (
                `Hi, I'd like to learn more about your services.` +
                `\n\nCould you please get in touch?`
            );
    }
}
